import React, { lazy, Suspense, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { TeamProvider } from './contexts/TeamContext';
import { ProjectsProvider } from './contexts/ProjectsContext';
import { InventoryProvider } from './contexts/InventoryContext';
import { ClientsProvider } from './contexts/ClientsContext';
import { QuotesProvider } from './contexts/QuotesContext';
import { InvoicesProvider } from './contexts/InvoicesContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ClientConfirmationsProvider } from './contexts/ClientConfirmationsContext';
import { NotificationProvider } from './contexts/NotificationContext';

import Layout from './components/Layout';

// Lazy load page components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const NewProject = lazy(() => import('./pages/NewProject'));
const EditProject = lazy(() => import('./pages/EditProject'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const ManualTimeEntry = lazy(() => import('./pages/ManualTimeEntry'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Team = lazy(() => import('./pages/Team'));
const Clients = lazy(() => import('./pages/Clients'));
const Quotes = lazy(() => import('./pages/Quotes'));
const NewQuote = lazy(() => import('./pages/NewQuote'));
const QuoteDetails = lazy(() => import('./pages/QuoteDetails'));
const EditQuote = lazy(() => import('./pages/EditQuote'));
const Invoices = lazy(() => import('./pages/Invoices'));
const NewInvoice = lazy(() => import('./pages/NewInvoice'));
const InvoiceDetails = lazy(() => import('./pages/InvoiceDetails'));
const ProjectHistory = lazy(() => import('./pages/ProjectHistory'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Presentation = lazy(() => import('./pages/Presentation'));
const Auth = lazy(() => import('./pages/Auth'));
const ClientConfirmation = lazy(() => import('./pages/ClientConfirmation'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Error boundary component
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="max-w-xl mx-auto p-8 bg-white rounded-lg shadow-lg">
            <div className="text-center">
              <div className="mb-6">
                <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
              <p className="text-gray-600 mb-4">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <AuthProvider>
            <OrganizationProvider>
              <SidebarProvider>
                <SettingsProvider>
                  <TeamProvider>
                    <ClientsProvider>
                      <QuotesProvider>
                        <InvoicesProvider>
                          <ProjectsProvider>
                            <ClientConfirmationsProvider>
                              <InventoryProvider>
                                <BrowserRouter>
                                  <Suspense fallback={<LoadingFallback />}>
                                  <Routes>
                                    <Route path="/auth" element={<Auth />} />
                                    <Route path="/presentation" element={<Presentation />} />
                                    <Route path="/accept-invitation" element={<AcceptInvitation />} />
                                    <Route path="/confirmations/:token" element={<ClientConfirmation />} />
                                    <Route path="/" element={<Layout />}>
                                      <Route index element={<Dashboard />} />
                                      <Route path="projects" element={<Projects />} />
                                      <Route path="projects/new" element={<NewProject />} />
                                      <Route path="projects/:id" element={<ProjectDetails />} />
                                      <Route path="projects/:id/edit" element={<EditProject />} />
                                      <Route path="projects/:id/time-entry" element={<ManualTimeEntry />} />
                                      <Route path="quotes" element={<Quotes />} />
                                      <Route path="quotes/new" element={<NewQuote />} />
                                      <Route path="quotes/:id" element={<QuoteDetails />} />
                                      <Route path="quotes/:id/edit" element={<EditQuote />} />
                                      <Route path="invoices" element={<Invoices />} />
                                      <Route path="invoices/new" element={<NewInvoice />} />
                                      <Route path="invoices/:id" element={<InvoiceDetails />} />
                                      <Route path="project-history" element={<ProjectHistory />} />
                                      <Route path="calendar" element={<Calendar />} />
                                      <Route path="inventory" element={<Inventory />} />
                                      <Route path="team" element={<Team />} />
                                      <Route path="clients" element={<Clients />} />
                                      <Route path="settings" element={<Settings />} />
                                      <Route path="*" element={<NotFound />} />
                                    </Route>
                                  </Routes>
                                </Suspense>
                              </BrowserRouter>
                            </InventoryProvider>
                          </ClientConfirmationsProvider>
                        </ProjectsProvider>
                      </InvoicesProvider>
                    </QuotesProvider>
                  </ClientsProvider>
                </TeamProvider>
              </SettingsProvider>
            </SidebarProvider>
          </OrganizationProvider>
        </AuthProvider>
      </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;